import { X } from "lucide-react";
import { useEffect, useId, useRef, type FormEvent, type ReactNode, type RefObject } from "react";
import { Button } from "@/components/ui";

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  note,
  size = "md",
  width,
  titleId: callerTitleId,
  dismissible = true,
  initialFocus,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** A small line under the title. */
  subtitle?: ReactNode;
  /** Shown above the title. */
  icon?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** Small print at the start of the footer, beside the buttons. */
  note?: ReactNode;
  size?: "md" | "lg";
  /** An exact width in px (never wider than the screen); by default `size` decides. */
  width?: number;
  /** The title's id, for content that also refers to it; unique per instance when omitted. */
  titleId?: string;
  /**
   * False for a dialog the user must answer (the session-expired sign-in): no close button, and Escape
   * does nothing. `onClose` is then only called if the dialog is closed some other way.
   */
  dismissible?: boolean;
  /** The control to focus when the dialog opens; by default the Close button (else the first control). */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Wraps the body and footer in a form, so a `type="submit"` button in the footer submits it. */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const generatedTitleId = `modal-title-${useId()}`;
  const titleId = callerTitleId ?? generatedTitleId;

  useEffect(() => {
    if (!open) {
      restoreFocusRef.current = null; // closed: the next opening remembers its own opener
      return;
    }
    const d = ref.current;
    if (!d) return;
    // Remember the opener once per opening and keep it until the dialog closes. StrictMode runs
    // this effect twice, and by the second run the dialog is modal: the page behind it is inert,
    // so focus can't be handed back in between and the active element is no longer the opener.
    if (!restoreFocusRef.current) {
      const active = document.activeElement;
      if (active instanceof HTMLElement && active !== document.body && !d.contains(active)) {
        restoreFocusRef.current = active;
      }
    }
    if (!d.open) {
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", ""); // environments without <dialog> support
    }
    const focusFirst = () => {
      const target =
        initialFocus?.current ??
        closeRef.current ??
        ref.current?.querySelector<HTMLElement>("input, select, textarea, button, [href]");
      target?.focus();
    };
    requestAnimationFrame(focusFirst);

    // Closing (or unmounting) hands focus back; by then the dialog is gone, so the page is not inert.
    return () => {
      const opener = restoreFocusRef.current;
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [open, initialFocus]);
  if (!open) return null;
  const content = (
    <>
      {children && <div className="space-y-4 px-5 py-4">{children}</div>}
      {footer && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">
          {note && <p className="min-w-0 flex-1 text-[11px] text-fg-subtle">{note}</p>}
          {footer}
        </div>
      )}
    </>
  );
  return (
    <dialog
      ref={ref}
      onClose={(e) => {
        if (dismissible) return onClose();
        // Some browsers close a dialog on a second Escape even after cancel was prevented: keep it up.
        const d = e.currentTarget;
        if (!d.open && typeof d.showModal === "function") d.showModal();
      }}
      onCancel={(e) => {
        e.preventDefault();
        if (dismissible) onClose();
      }}
      aria-labelledby={titleId}
      style={width ? { width: `min(${width}px, calc(100% - 2rem))` } : undefined}
      className={`m-auto ${width ? "" : `w-[calc(100%-2rem)] ${size === "lg" ? "max-w-xl" : "max-w-md"}`} rounded-xl border border-border bg-surface p-0 text-fg shadow-xl backdrop:bg-black/40`}
    >
      <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
        <div className="min-w-0">
          {icon && <div className="mb-3">{icon}</div>}
          <h2 id={titleId} className="text-base font-semibold">
            {title}
          </h2>
          {subtitle && <p className="mt-0.5 text-xs text-fg-subtle">{subtitle}</p>}
        </div>
        {dismissible && (
          <Button
            unstyled
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="rounded p-1 text-fg-subtle hover:bg-muted"
            aria-label="Close"
          >
            <X className="size-4" aria-hidden />
          </Button>
        )}
      </div>
      {onSubmit ? <form onSubmit={onSubmit}>{content}</form> : content}
    </dialog>
  );
}
